import React, { ReactNode } from "react";
import { MetricValue } from "@/features/investors/types/investors.types";
import {
    DeltaKind,
    investorsFormatters as fmt,
} from "@/features/investors/utils/investors-formatters";

const DELTA_ICONS: Record<DeltaKind, string> = {
    up: "arrow_upward",
    down: "arrow_downward",
    flat: "remove",
};

export type DeltaFormat = "pct" | "pp" | "bps";

const formatDelta = (metric: MetricValue, format: DeltaFormat): string => {
    if (format === "pct") {
        return metric.deltaPct === null ? "—" : fmt.deltaPct(metric.deltaPct);
    }
    if (metric.value === null || metric.previous === null) return "—";
    const difference = metric.value - metric.previous;
    return format === "pp" ? fmt.deltaPp(difference) : fmt.deltaBps(difference * 100);
};

const deltaDirection = (metric: MetricValue, format: DeltaFormat): DeltaKind => {
    if (format === "pct") return fmt.deltaKind(metric.deltaPct);
    if (metric.value === null || metric.previous === null) return "flat";
    return fmt.deltaKind(metric.value - metric.previous);
};

export const DeltaChip: React.FC<{
    metric: MetricValue;
    format?: DeltaFormat;
    invert?: boolean;
}> = ({ metric, format = "pct", invert = false }) => {
    const text = formatDelta(metric, format);
    const direction = deltaDirection(metric, format);
    const tone: DeltaKind =
        text === "—" ? "flat" : invert && direction !== "flat" ? (direction === "up" ? "down" : "up") : direction;

    return (
        <span className={`delta ${tone}`}>
            <span className="mso material-symbols-outlined">{DELTA_ICONS[direction]}</span>
            {text}
        </span>
    );
};

export const Unavailable: React.FC<{ reason?: string }> = ({ reason }) => (
    <span
        className="unavailable"
        data-tip={reason !== undefined ? reason : "This metric cannot be computed safely from the current data"}
    >
        Unavailable
    </span>
);

export const MetricText: React.FC<{
    metric: MetricValue;
    render: (value: number) => string;
}> = ({ metric, render }) =>
    metric.status === "unavailable" || metric.value === null ? (
        <Unavailable reason={metric.reason} />
    ) : (
        <>{render(metric.value)}</>
    );

export const KpiCapsule: React.FC<{
    label: string;
    value: ReactNode;
    spark?: ReactNode;
    delta?: ReactNode;
    deltaCaption?: string;
    reason?: string;
}> = ({ label, value, spark, delta, deltaCaption = "QoQ", reason }) => (
    <div className="kpi" data-tip={reason}>
        <div className="lbl">{label}</div>
        <div className="val">{value}</div>
        {spark !== undefined ? <div className="spark">{spark}</div> : null}
        <div className="row">
            {delta}
            <span style={{ fontSize: 11, color: "var(--sub)" }}>{deltaCaption}</span>
        </div>
    </div>
);

export const SectionHead: React.FC<{
    index: string;
    title: string;
    question?: ReactNode;
}> = ({ index, title, question }) => (
    <div className="sec-head">
        <span className="idx">{index}</span>
        <div>
            <h2>{title}</h2>
            {question !== undefined ? <div className="q">{question}</div> : null}
        </div>
    </div>
);

export const Insight: React.FC<{ warn?: boolean; children: ReactNode }> = ({ warn = false, children }) => (
    <div className={`insight ${warn ? "warn" : ""}`}>
        <span className="mso material-symbols-outlined">{warn ? "warning" : "auto_awesome"}</span>
        <div>{children}</div>
    </div>
);

/**
 * Insight body coming from the deterministic insights engine. The HTML is
 * produced exclusively by our own formatters — never from user input.
 */
export const InsightHtml: React.FC<{ html: string; warn?: boolean }> = ({ html, warn = false }) => (
    <Insight warn={warn}>
        <span dangerouslySetInnerHTML={{ __html: html }} />
    </Insight>
);

export const Micro: React.FC<{
    label: string;
    value: ReactNode;
    sub?: ReactNode;
    valueColor?: string;
}> = ({ label, value, sub, valueColor }) => (
    <div className="micro">
        <div className="l">{label}</div>
        <div className="v" style={valueColor !== undefined ? { color: valueColor } : undefined}>
            {value}
        </div>
        {sub !== undefined ? <div className="s">{sub}</div> : null}
    </div>
);

export const CardHeader: React.FC<{
    title: string;
    sub?: string;
    tag?: ReactNode;
}> = ({ title, sub, tag }) => (
    <div className="card-h">
        <div>
            <div className="ttl">{title}</div>
            {sub !== undefined ? <div className="sub">{sub}</div> : null}
        </div>
        {tag !== undefined ? <span className="tag">{tag}</span> : null}
    </div>
);
